/**
 * Built-in content for every editable page. The admin's saved edits
 * (CmsContent rows) are merged over these, so the site always renders even
 * with an empty database. `npm run db:seed-site` copies these into the CMS.
 *
 * Copy sources: AFS business profile + brochure PDFs and the business card.
 */

const img = (p) => `/images/${p}`;

export const defaultContent = {
  settings: {
    company: {
      name: 'Armed Fire Services',
      shortName: 'Armed Fire',
      tagline: 'Ensure 100% protection of human life & property',
      established: '2013',
      phone: '+91 97433 97209',
      phone2: '+91 90711 30999',
      whatsapp: '+91 97433 97209',
      email: 'anandan@armedfire.in',
      email2: 'operations@armedfire.in',
      email3: 'armedfireservices@gmail.com',
      address: '#293, Bekkarle Complex, 1st Floor, 6th Main, 11th Block, Marilingappa Extension, Nagarbhavi 2nd Stage, Bengaluru, Karnataka 560072',
      hours: '10:00 AM – 6:00 PM',
      days: 'Monday – Saturday',
      mapLink: 'https://www.google.com/maps?cid=13923347191753613478',
      mapEmbed: 'https://maps.google.com/maps?cid=13923347191753613478&output=embed',
    },
    google: {
      rating: '4.8',
      count: '27',
      mapsUrl: 'https://www.google.com/maps?cid=13923347191753613478',
      reviewUrl: 'https://www.google.com/maps?cid=13923347191753613478',
      showBadge: 'yes',
      sectionEyebrow: 'Google reviews',
      sectionTitle: 'What our customers say on Google',
    },
    brand: {
      logo: img('brand/logo.png'),
      headerSubtitle: 'Fire · Safety · Security',
      topbarText: 'Pan-India fire protection & safety solutions since 2013',
      quoteLabel: 'Get a Quote',
    },
    footer: {
      about:
        'Turnkey fire protection, security surveillance, AMC, safety audits and fire & safety training for high-rise buildings, corporates, hospitals, schools and manufacturing industries.',
      copyright: 'Armed Fire Services. All rights reserved.',
    },
    cta: {
      title: 'Is your building fire-safe and compliant?',
      text: 'Book a free site visit. Our engineers will assess your premises and recommend the right fire and security systems with a clear BOQ.',
      buttonLabel: 'Request a site visit',
    },
  },

  home: {
    hero: {
      eyebrow: 'Fire protection · Security · Training',
      titleStart: 'Ensure 100% protection of',
      titleHighlight: 'human life & property',
      text: 'Armed Fire Services takes turnkey fire and security projects from site visit, design and BOQ to erection, servicing and AMC — backed by 650+ projects across India.',
      primaryLabel: 'Get a free quote',
      secondaryLabel: 'Explore services',
      image: img('hero/firefighters-sunset.webp'),
      imageAlt: 'Firefighters spraying water into a blazing fire at sunset',
      badgeValue: 'Zero',
      badgeLabel: 'incidents — our goal for every site',
    },
    stats: [
      { value: '2013', label: 'Established' },
      { value: '650+', label: 'Projects across India' },
      { value: '5000+', label: 'Trainings conducted' },
      { value: '3 lakh+', label: 'People trained' },
    ],
    intro: {
      eyebrow: 'What we are',
      title: 'One partner from site visit to AMC',
      paragraph1:
        'Backed by a rich industry base and experienced personnel, Armed Fire Services has emerged as one of the eminent service providers and installers of quality fire safety equipment — used in offices, homes, buildings, hotels, industries and other premises to prevent fire accidents.',
      paragraph2:
        'We source quality materials from certified vendors and, with a skilled workforce, install and service every system to industrial standards.',
      image: img('services/sprinkler-pipework.webp'),
      imageAlt: 'Red fire sprinkler and hydrant pipework with valves and gauges',
      steps: ['Site visit', 'Design', 'BOQ preparation', 'Erection / execution', 'Services', 'AMC'],
    },
    services: {
      eyebrow: 'What we do',
      title: 'Fire fighting & security systems',
      text: 'We are engaged in installing our range of equipment in high-rise buildings, corporates and manufacturing industries by a team of experts and professionals.',
    },
    benefits: {
      eyebrow: 'Why it matters',
      title: 'A single fire can challenge the existence of a business',
      items: [
        { icon: 'shield-check', title: 'Improves safety', text: 'Systems and drills that keep people and premises protected every day.' },
        { icon: 'trending-down', title: 'Reduces fire losses', text: 'Early detection and fast suppression limit damage and downtime.' },
        { icon: 'users-round', title: 'Creates esprit-de-corps', text: 'Trained teams respond together, calmly and correctly.' },
        { icon: 'heart-handshake', title: 'Saves life & property', text: 'The reason we do this work — zero incidents for every human life.' },
      ],
    },
    why: {
      eyebrow: 'Why choose us',
      title: 'Built on quality, competence and team power',
      items: [
        { icon: 'target', title: 'Vision', text: 'Impart knowledge and skills on safety at home, office and factory, resulting in ZERO incident rates for every human life and property.' },
        { icon: 'award', title: 'Mission', text: 'Provide the best quality products that help our clients function in a very safe environment, with the best safety and protection.' },
        { icon: 'badge-check', title: 'Quality', text: 'We stay conversant with every major activity in the industry. When we take your project, we do full justice to it.' },
        { icon: 'heart-handshake', title: 'Values', text: 'Quality, price-conscious protection systems, honest and fair customer relations, and acting immediately on feedback.' },
        { icon: 'file-check-2', title: 'Experience & competence', text: 'A wide range of fire approvals and deep experience with fire standards, regulations and codes.' },
        { icon: 'hard-hat', title: 'Team power', text: 'A young, energetic team of engineers, designers, auditors, welders and skilled technicians.' },
      ],
    },
    founder: {
      eyebrow: 'Meet the founder',
      name: 'Anandan N',
      role: 'Managing Director',
      quote: 'Empowering businesses with reliable fire safety solutions that protect life and property.',
      text: 'A Master’s in Fire Technology Engineering from Mangalore University, certified by Karnataka Fire & Emergency Services, and a recipient of the national Atal Bharat Gaurav Samman award. With 15+ years in the field, he has conducted 5,000+ trainings impacting over 3 lakh people.',
      image: img('about/anandan-portrait.webp'),
      imageAlt: 'Portrait of Anandan N, Managing Director of Armed Fire Services',
    },
    clients: {
      eyebrow: 'Our clientele',
      title: 'Trusted by brands across India',
    },
    testimonials: {
      eyebrow: 'Testimonials',
      title: 'What our clients say',
    },
  },

  about: {
    hero: {
      eyebrow: 'About us',
      title: 'Ensuring zero incidents for human life and property',
      text: 'A Bengaluru-based fire protection and safety company, established in 2013, delivering turnkey projects across India.',
      image: img('hero/fire-crew.webp'),
      imageAlt: 'Fire crew fighting a large fire with hoses',
    },
    story: {
      eyebrow: 'Who we are',
      title: 'Fire safety equipment, installed and serviced right',
      paragraph1:
        'Backed by a rich industry base and experienced personnel, our company has emerged as one of the eminent service providers and installers of quality fire safety equipment. This equipment is extensively used in offices, homes, buildings, hotels, industries and other areas to prevent fire accidents.',
      paragraph2:
        'We value the health of our clients, so we source quality raw materials from certified vendors. With the help of a skilled workforce, we service every product to industrial standards. Owing to durable, high-performance and easy installation attributes, our service and installation of fire safety equipment is highly appreciated by our clients.',
      image: img('services/extinguisher-hose-cabinet.webp'),
      imageAlt: 'Fire extinguishers mounted next to a hose reel cabinet',
    },
    founder: {
      eyebrow: 'Managing Director',
      name: 'Anandan N',
      role: 'Managing Director, Armed Fire Services',
      paragraph1:
        'I hold a Master’s degree in Fire Technology Engineering from Mangalore University, with certifications from Karnataka Fire & Emergency Services and RA Mundkur Fire & Emergency Services, Bengaluru. I am a recipient of the national Atal Bharat Gaurav Samman Award under the Atma Nirbhar category.',
      paragraph2:
        'With 15+ years of experience, I have conducted 5,000+ trainings, impacting 3 lakh+ individuals, and received multiple motivator awards. Beyond my profession, I am a passionate long-distance rider (Kashmir to Kanyakumari, Leh–Ladakh), marathon runner, singer, motivational speaker, and chairman of educational institutions.',
      image: img('about/anandan-portrait.webp'),
      imageAlt: 'Portrait of Anandan N',
      awardImage: img('about/award-atal-bharat-gaurav.webp'),
      awardAlt: 'Anandan N receiving the Atal Bharat Gaurav Samman award on stage',
    },
    values: {
      eyebrow: 'Why do you choose us',
      title: 'Vision, mission and the values we work by',
    },
  },

  services: {
    hero: {
      eyebrow: 'Services',
      title: 'Fire fighting, security and safety — on a turnkey basis',
      text: 'We undertake installation and AMCs for all kinds of fire fighting equipment and security surveillance systems.',
      image: img('services/hose-cabinet.webp'),
      imageAlt: 'Red fire extinguisher cabinet against a concrete wall',
    },
    fire: {
      eyebrow: 'Fire fighting',
      title: 'Services & installations',
      text: 'Safety against fire matters at work places as well as at homes. Fire fighting equipment and proper fire protection ensure maximum protection — and keep the fire fighters who risk their lives safe too.',
    },
    security: {
      eyebrow: 'Security systems',
      title: 'Security & surveillance',
      text: 'We are professionals in high-tech surveillance and security systems in Bangalore. With the latest technology we meet the expectations of our clients all across India — at homes, offices and industrial premises.',
      image: img('services/cctv.webp'),
      imageAlt: 'Dome and bullet CCTV cameras on a pole',
    },
    audit: {
      eyebrow: 'Audit & consultancy',
      title: 'Safety audits, compliance and Fire NOC',
      text: 'Beyond equipment, our auditors help you build a safety culture and stay compliant with statutory requirements.',
      items: [
        'Risk management',
        'Develop safety manuals',
        'Industrial safety audits',
        'Develop safety procedures',
        'Safety culture improvement',
        'Develop emergency response plans',
        'EHS compliance evaluations',
        'Fire NOC / CC',
        'ISO 45001 OHSAS system design',
        'ISO 14001 EMS system design',
        'Incident investigations',
        'Energy audits',
        'Develop EHS legal registers',
        'External fire safety audits',
      ],
      image: img('services/safety-first.webp'),
      imageAlt: 'Gloved hands holding a Safety First card',
    },
    process: {
      eyebrow: 'How we work',
      title: 'Turnkey project delivery',
      steps: [
        { title: 'Site visit', text: 'Our engineers survey the premises, occupancy and risks.' },
        { title: 'Design', text: 'System layouts designed to NBC and IS standards.' },
        { title: 'BOQ preparation', text: 'A transparent bill of quantities and quotation.' },
        { title: 'Erection / execution', text: 'Installation by our in-house skilled technicians.' },
        { title: 'Testing & handover', text: 'Commissioning, testing and documentation.' },
        { title: 'Services & AMC', text: 'Ongoing maintenance to keep every system ready.' },
      ],
    },
  },

  amc: {
    hero: {
      eyebrow: 'AMC',
      title: 'Annual Maintenance Contracts',
      text: 'For projects we executed — and for existing installations irrespective of size and application (fire & CCTV).',
      image: img('services/amc-inspection.webp'),
      imageAlt: 'Inspector checking fire extinguishers with a clipboard',
    },
    intro: {
      eyebrow: 'Maintenance you can rely on',
      title: 'Your systems, always in working condition',
      paragraph1:
        'We not only undertake maintenance of projects and installations executed by us, but also existing projects irrespective of size and application (Fire & CCTV).',
      paragraph2:
        'We offer a one-year guarantee period on the purchase and installation of all our safety and security systems. Under this, we take care of any repairs or related issues for free. After a year, a satisfied client may enter into an annual maintenance contract (AMC) with us — and we take absolute care that every safety and security system stays in fine working condition.',
      image: img('services/extinguishers.webp'),
      imageAlt: 'Row of red fire extinguishers',
    },
    highlights: [
      { icon: 'badge-check', title: '1-year guarantee', text: 'Free repairs on everything we supply and install, for the first year.' },
      { icon: 'wrench', title: 'Any installation', text: 'We maintain systems installed by others too — fire and CCTV, any size.' },
      { icon: 'clipboard-check', title: 'Scheduled inspections', text: 'Periodic checks, servicing, refilling and test reports.' },
      { icon: 'siren', title: 'Emergency support', text: 'Fast response when a system needs attention.' },
    ],
    coverage: {
      eyebrow: 'What an AMC covers',
      title: 'Covered under our AMC',
      items: [
        'Fire extinguisher servicing, refilling & hydro-testing',
        'Fire hydrant & sprinkler system maintenance',
        'Fire alarm and smoke detection testing',
        'Gas leak detection system checks',
        'Suppression & flooding system servicing',
        'Pump room and piping inspection',
        'CCTV & surveillance system maintenance',
        'Access control & biometric servicing',
      ],
    },
  },

  training: {
    hero: {
      eyebrow: 'Training & development',
      title: 'Fire & safety training that saves lives',
      text: 'Training for corporate, government and private companies, schools and institutions — in the classroom and on the field.',
      image: img('training/school-drill.webp'),
      imageAlt: 'Students gathered for a fire evacuation mock drill',
    },
    intro: {
      eyebrow: 'Why train',
      title: 'Equipment is wasted without trained people',
      text: 'Big manufacturing industries and corporates invest heavily in equipment, apparatus, plant improvements and service programmes. All of this is wasted if properly trained manpower is not available. A single fire or industrial accident in any establishment can challenge the existence of the unit.',
      image: img('training/field-demo.webp'),
      imageAlt: 'Trainer demonstrating fire extinguisher use to staff outdoors',
      image2: img('training/classroom-1.webp'),
      image2Alt: 'Trainer leading a classroom fire safety session',
    },
    benefits: [
      { icon: 'shield-check', title: 'Improves safety' },
      { icon: 'trending-down', title: 'Reduces fire losses' },
      { icon: 'users-round', title: 'Creates esprit-de-corps' },
      { icon: 'heart-handshake', title: 'Saves life & property' },
    ],
    programmes: {
      eyebrow: 'Programmes',
      title: 'Armed Fire will give trainings',
      items: [
        'Basic Fire Life Safety Training',
        'Fire Panel Operation & Procedure Training',
        'ERT (Emergency Response Team) Roles & Responsibilities',
        'CMT (Crisis Management Team) Roles & Responsibilities',
        'First Aid Training (Basic with CPR)',
        'Disaster Management Training',
        'Kitchen Fire Safety Training',
        'Industrial Safety / Skills Training',
        'Fire Evacuation Mock Drill (Classroom & Planned Activity)',
        'Shop Floor Safety Training (Industrial Safety)',
        'HSE Management Training',
        'PPE Management Training',
        'Chemical & Gaseous Hazard Training',
        'Emergency Preparedness Training (as per Industrial Safety)',
        'OHS Training',
      ],
    },
    stats: [
      { value: '5000+', label: 'Trainings conducted' },
      { value: '3 lakh+', label: 'Individuals trained' },
      { value: '15+', label: 'Years of experience' },
    ],
  },

  clients: {
    hero: {
      eyebrow: 'Clients & testimonials',
      title: 'Excellent partnerships — and true friendships',
      text: 'We are very fortunate to have formed excellent partnerships with many of our clients. We have also formed true friendships.',
      image: img('hero/flames.webp'),
      imageAlt: 'Flames rising from a fire training tray',
    },
    logos: {
      eyebrow: 'Clientele',
      title: 'Some of the organisations we protect',
    },
    letters: {
      eyebrow: 'Recognition',
      title: 'Letters of appreciation',
      text: 'A few of the certificates and letters our clients have shared after working with us.',
    },
  },

  gallery: {
    hero: {
      eyebrow: 'Gallery',
      title: 'Our work in pictures',
      text: 'Installations, training sessions, mock drills and recognition from across our projects.',
      image: img('training/team-drill.webp'),
      imageAlt: 'Emergency response team during a drill',
    },
  },

  contact: {
    hero: {
      eyebrow: 'Contact',
      title: 'Let’s make your premises safer',
      text: 'Share your requirement and our team will get back to you within 48 hours.',
      image: img('hero/firefighter-spray.webp'),
      imageAlt: 'Firefighter spraying water at a fire',
    },
    form: {
      title: 'Request a quote or site visit',
      text: 'Tell us about your site and what you need. We usually respond the same working day.',
    },
  },
};
